<template>
    <div class="skip-logic-editor">
        <!-- Campo "If" - Muestra el campo actual -->
        <v-text-field
                class="skip-logic-editor__conditional-field"
                :value="fieldObj.title"
                outlined
                disabled
                dense
                hide-details
        >
            <template #prepend-inner>
                <component
                        :is="fieldConst.ICON_COMPONENT"
                />
                {{ fieldLocation }}
            </template>
        </v-text-field>

        <!-- Options Selection with autocomplete dropdown like conditional logic -->
        <div class="skip-logic-options">
            <f-autocomplete
                    v-model="selectedOptionsIndexes"
                    :items="allOptions"
                    :item-value="item => item.index"
                    :filter="(item, search) => item.text.toLocaleLowerCase().includes(search.toLocaleLowerCase())"
                    class="skip-logic-options__autocomplete"
                    ref="skipLogicAutocomplete"
                    placeholder="Add option"
                    :menu-props="{
                        maxWidth: optionsMenuWidth,
                        minWidth: optionsMenuWidth,
                        nudgeRight: optionsMenuNudgeRight,
                        nudgeBottom: 6,
                        closeOnClick: true
                    }"
                    multiple
                    outlined
                    deletable-chips
                    hide-details
                    dense
            >
                <template #prepend-inner>
                    <span class="skip-logic-options__inline-label" ref="skipLogicLabel">If selected</span>
                </template>
                <template #item="{on, item, attrs}">
                    <v-list-item v-on="on" v-bind="attrs">
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ formatOptionLabel(item) }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </template>
                <template #selection="{ item, parent }">
                    <v-chip
                            class="skip-logic-options__chip my-1"
                            :key="JSON.stringify(item)"
                            label
                            small
                    >
                        <span class="text-truncate">{{ formatOptionLabel(item) }}</span>
                        <XIcon class="cursor-pointer ml-1" size="12" @click="parent.selectItem(item)"/>
                    </v-chip>
                </template>
            </f-autocomplete>
        </div>

        <!-- Skip to destinations showing available questions -->
        <div v-if="selectedOptions.length > 0" class="skip-logic-destinations">
            <div class="skip-logic-destinations__label">Skip to</div>
            <div 
                v-for="option in selectedOptions" 
                :key="option.index"
                class="skip-logic-destination"
            >
                <DestinationSelector 
                    :option="option"
                    :current-field-id="fieldId"
                    :destination="getOptionDestination(option)"
                    :already-selected-field-ids="getOtherSelectedFieldIds(option)"
                    @update-destination="updateDestination"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import { reactive, computed, watch, ref, onMounted, onBeforeUnmount } from '@vue/composition-api'
    import { FieldsEnumerationGettersUtils } from '@/store/util_modules/fields_enumeration_getters'

    export default {
        name: "SkipLogicEditor",
        components: {
            DestinationSelector: () => import('./Elements/DestinationSelector'),
            XIcon: () => import('vue-feather-icons/icons/XIcon')
        },
        props: {
            fieldObj: {
                type: Object,
                required: true
            },
            fieldConst: {
                type: Object,
                required: true
            },
            fieldLocation: {
                type: String,
                required: true
            },
            fieldId: {
                type: String,
                required: true
            },
            initialSkipLogic: {
                type: Object,
                default: () => ({
                    type: 'skip_logic',
                    selectedOptions: [],
                    destinations: {}
                })
            }
        },
        setup(props, { emit, root, refs }) {
            // Get all available options from the field
            const allOptions = computed(() => {
                console.log('🔍 Computing allOptions for field:', props.fieldObj?.field_type)
                console.log('🔍 Field const:', props.fieldConst)
                console.log('🔍 Has CONDITIONAL_LOGIC:', !!props.fieldConst.CONDITIONAL_LOGIC)
                console.log('🔍 Has PARAMS:', !!props.fieldConst.CONDITIONAL_LOGIC?.PARAMS)
                console.log('🔍 PARAMS object:', props.fieldConst.CONDITIONAL_LOGIC?.PARAMS)
                console.log('🔍 Has VALUE_FORMATTER:', !!props.fieldConst.CONDITIONAL_LOGIC?.PARAMS?.VALUE_FORMATTER)
                
                if (props.fieldConst.CONDITIONAL_LOGIC && props.fieldConst.CONDITIONAL_LOGIC.PARAMS.VALUE_FORMATTER) {
                    const options = props.fieldConst.CONDITIONAL_LOGIC.PARAMS.VALUE_FORMATTER(props.fieldObj)
                    console.log('🎯 Generated options:', options)
                    return options
                }
                return []
            })

            // Menu width calculation for dropdown (like conditional logic)
            const optionsMenuWidth = ref(0)
            const optionsMenuNudgeRight = ref(0)
            let unwatchMenuWidth

            // Calculate dropdown width to only cover the chips area (like conditional logic)
            const calculateMenuWidth = () => {
                if (refs.skipLogicAutocomplete && refs.skipLogicLabel) {
                    const autocompleteWidth = refs.skipLogicAutocomplete.$el.getBoundingClientRect().width
                    const labelWidth = refs.skipLogicLabel.getBoundingClientRect().width
                    
                    optionsMenuWidth.value = autocompleteWidth - labelWidth - 32 // 32px for borders and padding
                    optionsMenuNudgeRight.value = labelWidth + 16 // 16px for padding
                }
            }

            // Skip logic state - Initialize with empty array to avoid timing issues
            const skipLogicState = reactive({
                selectedOptions: (() => {
                    // If there are existing selected options, use them
                    if (props.initialSkipLogic.selectedOptions && props.initialSkipLogic.selectedOptions.length > 0) {
                        return [...props.initialSkipLogic.selectedOptions]
                    }
                    // If no existing logic, start with empty array - will be populated by watch
                    return []
                })()
            })

            // Use ref for destinations to ensure proper reactivity
            const destinations = ref((() => {
                const dest = { ...(props.initialSkipLogic.destinations || {}) }
                console.log('🏁 Initial destinations:', dest)
                return dest
            })())

            // Track validation state for each option
            const optionValidationStates = ref({})

            // Watch for allOptions changes to auto-select all options for new skip logic
            watch(allOptions, (newOptions) => {
                console.log('🔍 allOptions changed:', newOptions)
                
                // Only auto-select if we have options AND no existing selectedOptions
                if (newOptions.length > 0 && 
                    skipLogicState.selectedOptions.length === 0 && 
                    (!props.initialSkipLogic.selectedOptions || props.initialSkipLogic.selectedOptions.length === 0)) {
                    console.log('🌟 Auto-selecting all options for new skip logic')
                    skipLogicState.selectedOptions = [...newOptions]
                }
            }, { immediate: true })

            // Debug: watch for changes in destinations
            watch(destinations, (newDestinations) => {
                console.log('🔄 Destinations changed:', newDestinations)
            }, { deep: true })

            // Computed property to check if all destinations are valid
            const isAllDestinationsValid = computed(() => {
                // Check if any selected options have invalid destinations
                for (const option of skipLogicState.selectedOptions) {
                    const validationState = optionValidationStates.value[option.index]
                    // If validation state is explicitly false, it's invalid
                    if (validationState === false) {
                        return false
                    }
                }
                return true
            })

            // Don't initialize with all options by default, keep empty if no initial data
            // This allows users to manually select which options they want for skip logic

            // Computed property for the f-autocomplete v-model (uses indexes)
            const selectedOptionsIndexes = computed({
                get: () => skipLogicState.selectedOptions.map(option => option.index),
                set: (indexes) => {
                    // Convert indexes back to option objects
                    const newSelectedOptions = indexes.map(index => 
                        allOptions.value.find(option => option.index === index)
                    ).filter(Boolean) // Remove any undefined values
                    
                    skipLogicState.selectedOptions = newSelectedOptions
                    
                    // Clean up destinations for removed options
                    const indexSet = new Set(indexes)
                    const newDestinations = {}
                    Object.keys(destinations.value).forEach(key => {
                        if (indexSet.has(parseInt(key))) {
                            newDestinations[key] = destinations.value[key]
                        }
                    })
                    destinations.value = newDestinations
                    
                    emitUpdate()
                }
            })

            // Get available fields for destination selection
            const availableFields = computed(() => {
                try {
                    const fields = root.$store.getters['edit/' + FieldsEnumerationGettersUtils.allFieldsWithEnumerationGetterName]
                    if (!fields) return []
                    
                    return Object.values(fields)
                        .filter(field => field && field.fieldId && field.fieldId !== props.fieldId)
                        .map(field => ({
                            field_id: field.fieldId,
                            title: field.fieldObj.title,
                            formattedLocation: field.formattedLocation,
                            fieldConst: field.fieldConst
                        }))
                } catch (error) {
                    console.warn('Error getting fields:', error)
                    return []
                }
            })

            // Update destination for an option
            const updateDestination = ({ option, destination, isValid }) => {
                console.log('💾 Updating destination:', { option: option.index, destination, isValid })
                
                // Create a new object to ensure Vue detects the change
                const newDestinations = { ...destinations.value }
                newDestinations[option.index] = destination
                destinations.value = newDestinations
                
                // Update validation state for this option
                const newValidationStates = {
                    ...optionValidationStates.value,
                    [option.index]: isValid
                }
                optionValidationStates.value = newValidationStates
                
                emitUpdate()
            }

            // Get destination for an option
            const getOptionDestination = (option) => {
                return destinations.value[option.index] || {
                    type: 'question',
                    field_id: undefined
                }
            }

            // Reactive ref that gets updated when destinations change
            const allSelectedFieldIds = computed(() => {
                const selectedIds = []
                Object.keys(destinations.value).forEach(optionIndex => {
                    const destination = destinations.value[optionIndex]
                    // Only track field IDs for question-type destinations
                    if (destination && destination.type === 'question' && destination.field_id) {
                        selectedIds.push(destination.field_id)
                    }
                })
                console.log('🔄 All selected field IDs computed:', selectedIds)
                return selectedIds
            })

            // Get already selected field IDs (excluding the current option being rendered)
            const getOtherSelectedFieldIds = (currentOption) => {
                const otherIds = allSelectedFieldIds.value.filter((fieldId) => {
                    // Find which option index this field_id belongs to
                    for (let optionIndex of Object.keys(destinations.value)) {
                        const destination = destinations.value[optionIndex]
                        if (destination && 
                            destination.type === 'question' && 
                            destination.field_id === fieldId && 
                            parseInt(optionIndex) !== currentOption.index) {
                            return true
                        }
                    }
                    return false
                })
                
                console.log(`🚫 Other selected field IDs for option ${currentOption.index}:`, otherIds)
                return otherIds
            }

            // Format option label (A. Yes, B. No, etc.)
            const formatOptionLabel = (option) => {
                const letter = String.fromCharCode(65 + option.index) // A, B, C, etc.
                
                // Special handling for star rating emoji options
                if (option.isEmoji && option.emoji) {
                    return `${letter}: ${option.emoji}`
                }
                
                return `${letter}. ${option.text}`
            }

            // Get field icon for the option (using current field's icon as placeholder)
            const getFieldIcon = (option) => {
                // For now, use the current field's icon, but this could be enhanced
                // to show different icons based on option type or destination
                return props.fieldConst.ICON_COMPONENT
            }

            // Get question text for the destination
            const getQuestionText = (option) => {
                const destination = getOptionDestination(option)
                
                // Handle special destination types
                if (destination.type === 'end_survey') {
                    return 'End of survey'
                }
                if (destination.type === 'thank_you') {
                    return 'Thank you page'
                }
                if (destination.type === 'redirect_url') {
                    return destination.redirect_url ? `Redirect URL: ${destination.redirect_url}` : 'Redirect URL'
                }
                
                // Handle regular question destinations
                if (destination.field_id) {
                    // Use the enumerated list directly like ConditionalTargetSelect
                    const enumeratedList = root.$store.getters['edit/' + FieldsEnumerationGettersUtils.enumeratedFieldsListGetterName]
                    const targetField = enumeratedList.find(field => field.fieldId === destination.field_id)
                    if (targetField) {
                        return `${targetField.formattedLocation}: ${targetField.fieldObj.title}`
                    }
                }
                
                return 'Select a destination'
            }

            // Emit updates to parent
            const emitUpdate = () => {
                const validState = isAllDestinationsValid.value
                emit('update-skip-logic', {
                    type: 'skip_logic',
                    selectedOptions: skipLogicState.selectedOptions,
                    destinations: destinations.value,
                    isValid: validState
                })
            }

            // Set up menu width calculation (like conditional logic)
            onMounted(() => {
                setTimeout(() => {
                    unwatchMenuWidth = watch(
                        () => skipLogicState.selectedOptions,
                        () => calculateMenuWidth(),
                        { deep: true, immediate: true }
                    )
                }, 300) // Wait for DOM to render
                
                // Initialize validation states for existing destinations
                console.log('🚀 Initializing validation states on mount...')
                console.log('📊 Initial destinations:', destinations.value)
                console.log('📊 Selected options:', skipLogicState.selectedOptions)
                
                // For each selected option, check if it has a valid destination
                skipLogicState.selectedOptions.forEach(option => {
                    const destination = destinations.value[option.index]
                    let isValid = true
                    
                    if (destination) {
                        if (destination.type === 'redirect_url') {
                            // Check if redirect URL is valid (not empty)
                            isValid = destination.redirect_url && destination.redirect_url.trim() !== ''
                        }
                        // For other destination types (question, end_survey, thank_you), assume valid
                    } else {
                        // No destination set, assume invalid
                        isValid = false
                    }
                    
                    optionValidationStates.value[option.index] = isValid
                    console.log(`🎯 Option ${option.index} initialized with validation state:`, isValid)
                })
                
                console.log('📋 Final validation states after initialization:', optionValidationStates.value)
                console.log('✅ Initial isAllDestinationsValid:', isAllDestinationsValid.value)
                
                // Emit initial update
                emitUpdate()
            })

            onBeforeUnmount(() => {
                if (unwatchMenuWidth) {
                    unwatchMenuWidth()
                }
            })

            return {
                selectedOptions: computed(() => skipLogicState.selectedOptions),
                updateDestination,
                getOptionDestination,
                formatOptionLabel,
                getFieldIcon,
                getQuestionText,
                allOptions,
                selectedOptionsIndexes,
                optionsMenuWidth,
                optionsMenuNudgeRight,
                allSelectedFieldIds,
                getOtherSelectedFieldIds,
                destinations,
                isAllDestinationsValid
            }
        }
    }
</script>

<style lang="scss" scoped>
    .skip-logic-editor {
        padding-left: 34px;
        &__conditional-field {
            margin-bottom: 20px !important;
            &:before {
                content: "If";
                position: absolute;
                align-self: center;
                right: calc(100% + 20px);

                font-size: 1.25rem;
                color: var(--v-bluish_gray_500-base);
            }

            ::v-deep .v-input__control {
                .v-input__slot {
                    .v-input__prepend-inner {
                        display: flex !important;
                        align-items: center !important;
                        color: var(--v-grey_700-base);

                        svg {
                            margin-right: 12px;
                        }
                        img {
                            width: 30px;
                            height: 30px;
                            margin-right: 12px;
                        }

                        &:after {
                            content: ':';
                            align-self: center;
                        }
                    }

                    .v-text-field__slot {
                        input {
                            color: var(--v-grayscale_black-base) !important;
                            font-weight: 500 !important;
                        }
                    }
                }
            }
        }
    }

    .skip-logic-options {
        margin-bottom: 24px;
        &__autocomplete {
            ::v-deep .v-input__control {
                .v-input__slot {
                    display: flex !important;
                    padding: 0 !important;
                    .v-input__prepend-inner {
                        display: flex !important;
                        align-items: center !important;
                        align-self: stretch !important;
                        justify-content: center !important;
                        width: 120px !important;
                        background-color: #f2f5ff !important;
                        font-size: 14px !important;
                        font-weight: 500 !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        border-right: 1px solid rgba(0, 0, 0, 0.12) !important;
                        border-radius: 8px 0 0 8px !important;
                    }

                    .v-select__slot {
                        padding-left: 12px !important;
                        
                        .v-select__selections {
                            flex-wrap: wrap !important;
                            gap: 6px !important;
                            padding: 5px 0 !important;
                        }
                    }
                }

                // Agregar margin-right al icono del chevron
                .v-input__append-inner {
                    margin-right: 12px !important;
                    
                    .v-input__icon {
                        margin-right: 0 !important;
                    }
                }
            }
        }

        &__chip {
            border-radius: 20px !important;
            font-size: 0.875rem !important;
            height: 24px !important;
            background-color: #f5f6f8 !important;
        }

        &__inline-label {
            font-size: 0.875rem;
            color: var(--v-bluish_gray_600-base);
            font-weight: 500;
            white-space: nowrap;
        }
    }

    .skip-logic-destinations {
        &__label {
            font-size: 1.25rem;
            color: var(--v-bluish_gray_500-base);
            margin-bottom: 16px;
            font-weight: 400;
        }
    }

    .skip-logic-destination {
        margin-bottom: 12px;

        &__icon {
            width: 24px;
            height: 24px;
            color: var(--v-bluish_gray_600-base);
        }

        &__option-text {
            font-weight: 500;
            color: var(--v-bluish_gray_700-base);
            font-size: 0.875rem;
        }
    }
</style> 